import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

function daysSince(iso) {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return Infinity;
  return (Date.now() - then) / (1000 * 60 * 60 * 24);
}

export default function PwaInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already installed?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (daysSince(dismissedAt) < DISMISS_DAYS) return;

    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    setIsIos(ios);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari doesn't fire beforeinstallprompt — show manual banner after 5s
    if (ios) {
      const t = setTimeout(() => setVisible(true), 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[100] rounded-2xl shadow-2xl bg-[#17301C] text-white p-4 flex items-start gap-3 border-2 border-[#EAB308]"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Installa app SolarControl Minniti"
    >
      <img src="/logo.jpg" alt="SolarControl Minniti" className="w-12 h-12 rounded-xl bg-white p-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-black text-base leading-tight mb-1">Installa l&apos;app</div>
        {isIos ? (
          <div className="text-xs text-white/85 leading-relaxed">
            Tocca <Share size={12} className="inline align-text-bottom" /> Condividi in basso, poi
            <b className="text-[#EAB308]"> &ldquo;Aggiungi a Home&rdquo;</b>.
          </div>
        ) : (
          <div className="text-xs text-white/85 leading-relaxed">
            Aggiungi SolarControl al tuo dispositivo per accesso rapido e offline.
          </div>
        )}
        {!isIos && (
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EAB308] text-[#17301C] font-black text-sm hover:bg-[#f5bc1b] transition-colors"
          >
            <Download size={14} /> Installa
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        data-testid="pwa-dismiss-btn"
        aria-label="Chiudi"
        className="flex-shrink-0 p-1.5 rounded-full hover:bg-white/10 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}
